import type { AIRealmAdmin } from '../types/gotypes'
import { adminNames } from './aiAccess'

/**
 * Why a user cannot do something, in one sentence they can act on (Adam,
 * 8 Oct). A page never hides an action the user cannot do: it shows it
 * disabled with this reason, which says what they can't do, who to ask, and,
 * where a copy path exists, that they can make their own copy.
 *
 *   "You can't rename this shared table. Ask Jo Smith, your team manager, or make your own copy."
 *   "You can't rename a colleague's table. Ask jo@sky.com, who owns it, or make your own copy."
 *
 * The rules themselves stay with each surface (they mirror the server's);
 * this file only words the answer. Who to ask comes from GET /ai-access/:customer/me
 * (useWhoToAsk), which names the caller's team managers whether or not the
 * AI access switch is on.
 */

export type ActionDecision = { allowed: true; reason?: undefined } | { allowed: false; reason: string }

export const ALLOWED: ActionDecision = { allowed: true }

/** Who the user asks about something they cannot do. */
export interface WhoToAsk {
  /** The managers of the user's team, by name where licensing knows one. */
  teamManagers: AIRealmAdmin[]
  /** The user is in a team, even when it has no manager appointed. */
  inTeam: boolean
}

/** Until /me answers: the sentence says "a Manager". */
export const NOBODY_KNOWN: WhoToAsk = { teamManagers: [], inTeam: false }

export interface RefuseOptions {
  /**
   * The item's owner. A colleague's item names them: they can change it,
   * a team manager often cannot.
   */
  owner?: string
  /**
   * A copy path exists. `true` adds "or make your own copy" (the copy action
   * sits next to the disabled one); a string names the control that makes
   * the copy when its label says something else: "or use Duplicate to make
   * your own copy".
   */
  copy?: boolean | string
}

const named = (people: AIRealmAdmin[]) => people.filter((p) => p.name || p.email)

/** "jo@sky.com, who owns it" / "Jo Smith, your team manager" / "your team manager" / "a Manager". */
export function whomToAsk(who: WhoToAsk, owner?: string): string {
  if (owner) return `${owner}, who owns it`
  const managers = named(who.teamManagers)
  if (managers.length) return `${adminNames(managers)}, your team manager${managers.length > 1 ? 's' : ''}`
  return who.inTeam ? 'your team manager' : 'a Manager'
}

/** "Ask Jo Smith, your team manager, or make your own copy." */
export function askSentence(who: WhoToAsk, opts: RefuseOptions = {}): string {
  const whom = whomToAsk(who, opts.owner)
  const copy = !opts.copy ? '' : typeof opts.copy === 'string' ? `, or use ${opts.copy} to make your own copy` : ', or make your own copy'
  return `Ask ${whom}${copy}.`
}

/** A refusal: what the user can't do, then who to ask. `cannot` is one sentence: "You can't rename this shared table." */
export function refuse(cannot: string, who: WhoToAsk, opts: RefuseOptions = {}): { allowed: false; reason: string } {
  return { allowed: false, reason: `${cannot} ${askSentence(who, opts)}` }
}

/** ALLOWED when `allowed`, else the refusal. */
export function decide(allowed: boolean, cannot: string, who: WhoToAsk, opts: RefuseOptions = {}): ActionDecision {
  return allowed ? ALLOWED : refuse(cannot, who, opts)
}

/**
 * Why ThemeAI cannot add sentiment or emotion: a Manager switched it off for
 * the customer (#1234 SKY-20, Admin → AI → AI models in use).
 */
export function themeAIPassOffReason(pass: 'sentiment' | 'emotion'): string {
  return refuse(`You can't add ${pass}: it is switched off for this customer.`, NOBODY_KNOWN).reason
}
